'use client'

import * as React from 'react'
import { useRef, useEffect } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CheckboxProps {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
  indeterminate?: boolean
  className?: string
  id?: string
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  onCheckedChange,
  label,
  description,
  disabled = false,
  indeterminate = false,
  className,
  id,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const checkboxElement = (
    <div className="relative flex items-start">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        disabled={disabled}
        className="sr-only"
        id={id}
      />
      <div
        className={cn(
          'w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer flex-shrink-0',
          checked || indeterminate
            ? 'bg-[var(--color-lime)] border-[var(--color-lime)] text-[var(--primary-foreground)]'
            : 'border-muted-foreground hover:border-[var(--color-lime)]',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => !disabled && onCheckedChange?.(!checked)}
      >
        {indeterminate ? (
          <Minus className="w-3 h-3" />
        ) : checked ? (
          <Check className="w-3 h-3" />
        ) : null}
      </div>
    </div>
  )

  if (!label) {
    return <div className={className}>{checkboxElement}</div>
  }

  return (
    <label
      className={cn(
        'flex items-start gap-3 cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={(e) => {
        e.preventDefault()
        if (!disabled) onCheckedChange?.(!checked)
      }}
    >
      {checkboxElement}
      <div className="space-y-0.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    </label>
  )
}

export { Checkbox }
